import { LegalPageLayout, Section } from "../components/legal/LegalPageLayout";
import { Link } from "react-router-dom";

function DateCallout({ date, text }: { date: string; text: string }) {
  return (
    <div
      className="my-4 rounded-lg border-l-4 p-4"
      style={{
        borderColor: "var(--color-primary)",
        background: "var(--color-surface-secondary)",
      }}
    >
      <p className="font-semibold text-[var(--color-text-primary)]">{date}</p>
      <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{text}</p>
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-2">
      <span className="mt-0.5 h-1.5 w-1.5 shrink-0 translate-y-1.5 rounded-full bg-[var(--color-primary)]" />
      <span>{children}</span>
    </li>
  );
}

export function BlogEichrechtKonformitaetChecklist() {
  return (
    <LegalPageLayout
      title="Eichrecht Konformität 2026: Die 8-Punkte-Checkliste für CPOs"
      lastUpdated="30. April 2026"
      crossLink={{ to: "/blog", label: "Zurück zur Übersicht" }}
      contactEmail="contact@chargesuite.de"
    >
      {/* Lede — Tesla case + €50K + €100K stack */}
      <Section
        id="lede-bussgeld-stack"
        title="50.000 plus 100.000 Euro: Warum Eichrecht 2026 zum Pflichtprojekt wird"
      >
        <p>
          Im August 2022 wurden über 1.800 Tesla Supercharger in Deutschland
          öffentlich als nicht eichrechtskonform identifiziert. Tesla
          berichtet seither quartalsweise an das Landesamt für Mess- und
          Eichwesen Berlin-Brandenburg über den Umrüstungsfortschritt. Das
          maximale Bußgeld pro Säule nach <strong>§ 60
          Messgerätegesetz</strong>: 50.000 Euro. Im Fall Tesla wurde dieses
          Maximum nicht ausgeschöpft. Bei einem Operator ohne den
          medien-strategischen Hintergrund eines OEMs ist das keine
          verlässliche Annahme.
        </p>
        <p>
          Ab 2026 stapelt sich das Risiko: Mit dem
          Preisangabengesetz-Kabinettsentwurf vom 25. März 2026 erhält die
          Bundesnetzagentur einen neuen Bußgeldrahmen{" "}
          <strong>bis zu 100.000 Euro</strong> für Verstöße gegen die
          AFIR-Preisangabepflichten an öffentlich zugänglichen Ladepunkten.
          Eichrechtlich nicht-konforme Hardware kann die kWh-basierte
          Preisangabe der AFIR Artikel 5 Absatz 4 oft gar nicht prüffest
          erbringen. Operator riskieren also dieselbe Sitzung, denselben
          Sachverhalt, zwei Bußgeldspuren: § 60 MessEG (bis 50.000 Euro pro
          Tatbestand) <em>plus</em> PAngV-Novelle (bis 100.000 Euro pro
          Verstoß).
        </p>
        <p>
          Der dritte Hebel ist zivilrechtlich: § 33 Absatz 1
          Messgerätegesetz verpflichtet den Operator, dem Kunden die
          Rechnungslegung &bdquo;in einfacher Weise zur Überprüfung&ldquo;
          nachvollziehbar zu machen. Ohne signiertes Datenpaket aus dem
          Zähler ist die Forderung angreifbar. Verbraucherzentralen haben
          dies seit 2021 ausdrücklich genutzt; einzelne Operatoren mussten
          rückwirkend Erstattungen leisten. Diese Checkliste fasst
          zusammen, woran ein Operator die Eichrechts-Konformität seiner
          Flotte 2026 prüft &mdash; mit konkreten Paragraphenankern und ohne
          die Mythen, die in älteren Quellen kursieren.
        </p>
      </Section>

      {/* Statutory framework */}
      <Section
        id="statutory-framework"
        title="Der gesetzliche Rahmen: MessEG, MessEV, PTB-A 50.7"
      >
        <p>
          Eichrecht ist in Deutschland in drei Schichten organisiert. Erstens
          das Messgerätegesetz (<strong>MessEG</strong>): § 3 definiert
          Messgeräte als alle Geräte, die zur Verwendung im
          geschäftlichen Verkehr bestimmt sind &mdash; öffentliches Laden
          fällt darunter. § 31 verpflichtet den Verwender, nur geeichte
          Messgeräte einzusetzen und Wartungsnachweise aufzubewahren bis
          drei Monate nach Ablauf der Eichgültigkeit, höchstens fünf Jahre.
          § 33 verlangt nachvollziehbare Rechnungen mit geeigneten
          Hilfsmitteln zur Überprüfung. § 60 fasst den Bußgeldrahmen: bis
          50.000 Euro für Verstöße gegen Inverkehrbringen ohne
          Konformität, unzulässige Verwendung und Nichteinhaltung
          wesentlicher Anforderungen, sonst bis 20.000 Euro.
        </p>
        <p>
          Zweitens die Mess- und Eichverordnung (<strong>MessEV</strong>): Sie
          konkretisiert die wesentlichen Anforderungen, das Verfahren der
          Konformitätsbewertung und die Form der Eichkennzeichnung. Drittens
          die <strong>PTB-A 50.7</strong>: Die Anforderungsspezifikation der
          Physikalisch-Technischen Bundesanstalt für elektronische und
          softwaregesteuerte Messgeräte in der Elektrizität. Sie definiert
          unter anderem, dass die Ziffernhöhe metrologisch relevanter
          Anzeigen mindestens acht Millimeter betragen muss und dass Daten,
          die nicht physisch am Zähler dargestellt werden, kryptografisch
          gesichert und über ein Remote Display zugänglich sein müssen.
        </p>
        <p>
          Wichtig: Die alte 6-Wochen-Anzeigepflicht nach § 32 MessEG &mdash;
          die in vielen älteren Branchenleitfäden noch zitiert wird &mdash;
          wurde durch das Vierte Bürokratieentlastungsgesetz zum{" "}
          <strong>1. Januar 2025 abgeschafft</strong>. Wer einen Leitfaden
          mit § 32 als aktiver Pflicht liest, liest eine veraltete Quelle.
          Die Eichpflicht selbst bleibt unberührt; nur die formelle Anzeige
          gegenüber der Eichbehörde entfällt.
        </p>
      </Section>

      {/* OCMF signature chain */}
      <Section
        id="ocmf-signaturkette"
        title="Die Signaturkette: Warum Backend-Signaturen Eichrecht-Killer sind"
      >
        <p>
          Eichrechtskonforme Abrechnung in Deutschland funktioniert
          ausschließlich, wenn die Signatur der Messdaten <em>im Zähler
          selbst</em> entsteht und nicht im Backend. Das Open Charge Metering
          Format (OCMF) der S.A.F.E. e.V. spezifiziert dafür die
          Pflichtfelder im signierten Datensatz: <code>RV</code> (Reading
          Value), <code>RI</code> (OBIS-Code), <code>RU</code> (Einheit, in
          der Regel kWh), <code>TM</code> (ISO-8601-Zeitstempel mit
          Synchronstatus), <code>TX</code> (B für Begin oder E für End),{" "}
          <code>ST</code> (Statusflag), <code>MS</code> (Meter Serial),
          <code>GS</code> (Gateway Serial) und <code>PG</code> (Pagination
          mit T-Präfix).
        </p>
        <p>
          Der OCMF-Spezifikationstext ist eindeutig: <em>&bdquo;the contents
          of the payload data section must not be modified between signing
          and verification.&ldquo;</em> Das hat eine harte Konsequenz für die
          CPMS-Architektur: Wenn das Backend einen Datensatz empfängt,
          umstrukturiert und mit eigenem Schlüssel neu signiert, ist die
          Eichrechtskonformität gebrochen. Die Transparenzsoftware der
          S.A.F.E. zeigt in diesem Fall einen ungültigen Hash &mdash; und
          jede Prüfung im behördlichen Termin scheitert sofort.
        </p>
        <p>
          Das OCPP-Eichrecht-Whitepaper der Open Charge Alliance (Februar
          2025) bestätigt diese Architekturentscheidung: Charge Detail
          Records müssen signierte Zählerstände für Begin und End
          enthalten. Ändert sich der Tarif während der Sitzung, sind
          Zwischenwerte verpflichtend. Der signierte Datensatz wird vom CPMS
          unverändert weitergeleitet, nicht übersetzt, nicht reassembliert,
          nicht gekürzt. Wer einen CPMS-Anbieter prüft, fragt deshalb nicht
          &bdquo;unterstützt das System OCMF?&ldquo; sondern: &bdquo;Wird der
          OCMF-Datensatz vom Backend unverändert weitergeleitet oder neu
          erzeugt?&ldquo;
        </p>
      </Section>

      {/* The 8-step checklist */}
      <Section
        id="checkliste-8-punkte"
        title="Die 8-Punkte-Checkliste: Eichrecht-Konformität 2026"
      >
        <p>
          Diese Liste bildet den Ablauf einer Eichrechts-Selbstprüfung ab,
          den ein Operator mit jeder Säule der Flotte einzeln durchgehen
          sollte. Jeder Punkt mit Paragraphenanker, jeder Punkt
          dokumentierbar.
        </p>
        <ul className="ml-2 mt-3 space-y-2">
          <Bullet>
            <strong>1. PTB-Baumusterprüfbescheinigung vorhanden und
            gültig.</strong> Prüfen: Zulassungsnummer in der
            PTB-Zulassungsdatenbank suchbar, Modellbezeichnung deckungsgleich
            mit Hardware vor Ort, Zulassungszeitraum nicht abgelaufen.
            Rechtsgrundlage: § 6 MessEG plus PTB-A 50.7. Beispiele konformer
            Hardware: Compleo SAM-Modul, Mennekes Amtron Professional und
            Amedio Professional, Alpitronic HYC150 (DC). Nicht konform:
            ABL eMH1 (laut Herstellerdokumentation).
          </Bullet>
          <Bullet>
            <strong>2. Display vor Ort: kWh-Anzeige mit korrekter
            Ziffernhöhe.</strong> Mindestziffernhöhe acht Millimeter für
            metrologisch relevante Werte (PTB-A 50.7). Echtzeit-Update
            innerhalb von zehn Sekunden. Mindestauflösung 0,1 kWh. Wenn
            Werte nicht am Zähler darstellbar sind, muss ein Remote
            Display am Stationsterminal kryptografisch gesicherte
            Anzeige liefern.
          </Bullet>
          <Bullet>
            <strong>3. Signaturkette: Signatur entsteht im Zähler, nicht im
            Backend.</strong> OCMF-Datensatz mit allen Pflichtfeldern.
            Signaturschlüssel im sicheren Element (Secure Element, HSM oder
            zertifiziertes Smart Meter Gateway). Prüfbar: Datensatz von der
            Säule per Transparenzsoftware verifizieren. Grüner Haken
            erforderlich.
          </Bullet>
          <Bullet>
            <strong>4. Plombierung und Manipulationsschutz unverletzt.</strong>{" "}
            Eichplombe sichtbar und unbeschädigt. Bei Verletzung &mdash; etwa
            durch Servicearbeiten am Zähler &mdash; ist eine Nacheichung
            notwendig vor Wiederinbetriebnahme.
          </Bullet>
          <Bullet>
            <strong>5. Transparenzsoftware oder Datenexport gemäß § 33
            MessEG.</strong> Operator stellt Kunden geeignete Hilfsmittel zur
            Verifikation bereit. Praktisch: S.A.F.E.-Transparenzsoftware oder
            ein OCMF-Datenexport aus dem CPMS, den der Kunde gegenprüfen
            kann.
          </Bullet>
          <Bullet>
            <strong>6. Wartungs- und Eichdokumentation aktuell.</strong>{" "}
            Aufbewahrung von Eichscheinen, Wartungsnachweisen und
            Konformitätsdokumentation bis drei Monate nach Ablauf der
            Eichgültigkeit, höchstens fünf Jahre nach § 31 MessEG.
            (Die häufig zitierte 10-Jahre-Frist gilt für Hersteller im
            Inverkehrbringen, nicht für den CPO als Verwender.)
          </Bullet>
          <Bullet>
            <strong>7. Preisanzeige im Einklang mit AFIR Artikel 5 Absatz
            4.</strong> Der Ad-hoc-Preis bei Ladepunkten ab 50 kW basiert
            primär auf Kilowattstunden. Zeitkomponenten nur als
            Belegungsentgelt. Wer hier zeitprimär abrechnet, riskiert
            zusätzlich zum Eichrechtsverstoß die PAngV-Novelle bis
            100.000 Euro.
          </Bullet>
          <Bullet>
            <strong>8. V2G-Sondercheck: Bidirektionale Eichrechtszulassung
            separat.</strong> Eine unidirektionale PTB-Zulassung deckt
            Rückspeisung nicht ab, auch wenn die Hardware sie technisch
            kann. Mit dem Bundestagsbeschluss vom 13. November 2025 öffnet
            sich V2G regulatorisch &mdash; ohne separate
            Konformitätsbewertung kein konformer Bezug-Rückspeise-Betrieb.
          </Bullet>
        </ul>
      </Section>

      {/* AFIR-Eichrecht convergence + civil exposure */}
      <Section
        id="afir-eichrecht-zusammenfuehrung"
        title="AFIR und Eichrecht: Wo sie konvergieren, wo sie reiben"
      >
        <p>
          AFIR Artikel 5 Absatz 4 verlangt kWh-primäre Abrechnung; das
          deutsche Eichrecht verlangt geeichte kWh-Messung. An dieser Stelle
          arbeiten beide Regelwerke in dieselbe Richtung. Die Reibung
          entsteht im Detail: Die AFIR fordert pauschale Belegungsentgelte
          als ergänzendes Element zur Verhinderung von Dauerblockaden &mdash;
          eichrechtlich muss diese Komponente sauber von der Energiekomponente
          getrennt sein, damit die Rechnung gemäß § 33 MessEG nachvollziehbar
          bleibt. Wer Belegungsgebühr und Energiekomponente in einer
          aggregierten Endsumme darstellt, scheitert eichrechtlich an der
          Nachvollziehbarkeit, AFIR-rechtlich an Artikel 7 Preistransparenz,
          und gegebenenfalls zivilrechtlich an § 33 MessEG.
        </p>
        <p>
          Zivilrechtliche Konsequenz: Erfüllt ein Operator § 33 MessEG nicht,
          ist die Forderung entkräftbar. Die Verbraucherzentrale forderte
          2021 öffentlichkeitswirksam pauschale 20-Prozent-Preisreduktionen
          bei nicht-konformen Operatoren. Bindende höchstrichterliche
          Rechtsprechung fehlt; einzelne Amtsgerichte gaben Verbrauchern
          aber recht. Das materielle Risiko entsteht aus Mengen: bei einer
          Flotte mit 200 Säulen und 30 Sitzungen pro Säule pro Monat
          summieren sich Erstattungen schnell.
        </p>
      </Section>

      {/* CTA */}
      <Section id="cta" title="Nächster Schritt: Eichrechts-Selbstaudit">
        <p>
          ChargeSuite bietet einen kostenlosen Eichrechts-Selbstaudit, der die
          oben aufgeführten acht Punkte für Ihre Flotte konsolidiert.
          Eingabe: Hardwaremodelle, Zulassungsnummern, CPMS-Anbieter. Ausgabe:
          ein PDF-Prüfbericht mit konkretem Handlungsbedarf, sortiert nach
          rechtlicher Dringlichkeit (§ 60 MessEG-Bußgeld vor PAngV-Bußgeld
          vor zivilrechtlichem Risiko). Der Bericht eignet sich als
          Gesprächsgrundlage für Hardware-Lieferanten und CPMS-Vendoren.
        </p>
        <p>
          Unser Compliance Brain überwacht die laufende Eichgültigkeit, die
          Veröffentlichung neuer PTB-Zulassungen und die Änderungen an
          MessEG/MessEV automatisch. Sie werden alarmiert, bevor eine
          Eichgültigkeit abläuft, nicht erst, wenn ein Kunde reklamiert.
        </p>
        <p className="mt-4">
          <Link
            to="/audit"
            style={{ color: "var(--color-primary)" }}
            className="font-semibold hover:underline"
          >
            Jetzt kostenlosen Eichrechts-Selbstaudit starten &rarr;
          </Link>{" "}
          oder{" "}
          <Link
            to="/pricing"
            style={{ color: "var(--color-primary)" }}
            className="font-semibold hover:underline"
          >
            Preise und Pakete vergleichen
          </Link>
          .
        </p>

        <DateCallout
          date="1. Januar 2025"
          text="§ 32 MessEG (6-Wochen-Anzeigepflicht) wurde durch das Vierte Bürokratieentlastungsgesetz abgeschafft. Ältere Leitfäden, die diese Pflicht zitieren, sind veraltet."
        />
      </Section>
    </LegalPageLayout>
  );
}
